import { locationRoute, menuRoute } from "@/App";
import { Hero } from "@/components/Hero";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { useLocationMenus } from "@/hooks/useLocationMenus";
import { useTables } from "@/hooks/useTables";
import { orderTypeOptions } from "@/lib/orderTypes";
import { useLocation } from "@/providers/LocationContextProvider";
import { MenuType } from "@/types/menu";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLocationInfo } from "@/hooks/useLocationInfo";

import { useEffect, useMemo, useState } from "react";

export const LocationPage = () => {
  const {
    tableId,
    name,
    orderType,
    setName,
    setOrderType,
    setTableId,
    setSelectedMenu,
  } = useLocation();

  const search = useSearch({
    from: locationRoute.to,
  }) as {
    table?: string;
  };

  const { table: queryTable } = search;

  const { locationId } = locationRoute.useParams();
  const { data: tables = [] } = useTables(locationId, true);

  const { availableMenus, hasMultipleMenus, hasTakeAwayMenu, hasDineInMenu } =
    useLocationMenus();
  const { data: locationInfo } = useLocationInfo(locationId);

  const hasTables = useMemo(() => {
    return tables.length > 0;
  }, [tables]);

  const selectedTable = useMemo(() => {
    return tables.find((table) => table.id === tableId);
  }, [tables, tableId]);

  useEffect(() => {
    if (locationInfo && (!name || name !== locationInfo.name)) {
      setName(locationInfo.name);
    }
  }, [locationInfo, name]);

  const navigate = useNavigate();

  const canSelectOrderType = useMemo(() => {
    return hasTakeAwayMenu && hasDineInMenu;
  }, [hasTakeAwayMenu, hasDineInMenu]);

  const initialStep = useMemo(() => {
    console.log("Determining initial step based on conditions", {
      canSelectOrderType,
      hasDineInMenu,
      tableId,
      queryTable,
      orderType,
    });
    if (orderType) {
      console.log("Order type already set, skipping initial step selection");
      return "MENU";
    }

    if (queryTable !== undefined) {
      // If a table is selected, we should skip the order type selection
      console.log("Table selected, skipping order type selection");
      return "MENU";
    }

    if (canSelectOrderType) {
      console.log(
        "canSelectOrderType is true, setting initial step to ORDER-TYPE"
      );
      return "ORDER-TYPE";
    }

    if (hasDineInMenu && tableId === undefined) {
      console.log("hasDineInMenu & no tableId, setting initial step to TABLE");
      return "TABLE";
    }

    console.log("Setting initial step to MENU");
    return "MENU";
  }, [canSelectOrderType, hasDineInMenu]);

  type Step = "ORDER-TYPE" | "TABLE" | "MENU";

  const [step, setStep] = useState<Step>(initialStep);

  const handleOrderTypeSelection = (type: MenuType) => {
    console.log("handleOrderTypeSelection", type);
    setOrderType(type);
    if (type === "TAKE_AWAY") {
      setStep("MENU");
      setTableId("");
    }
    if (type === "DINE_IN") {
      setStep("TABLE");
    }
  };

  const handleTableSelection = (tableId: string) => {
    setTableId(tableId);
    setStep("MENU");
  };

  useEffect(() => {
    if (step === "MENU" && !hasMultipleMenus) {
      console.log("No multiple menus, selecting first menu");
      handleMenuSelection(availableMenus[0].id);
    }
  }, [step]);

  const handleMenuSelection = (menuId: string) => {
    console.log("handleMenuSelection", menuId);
    setSelectedMenu(menuId);
    navigate({
      to: menuRoute.to,
      params: { menuId },
      search: {
        table: selectedTable ? selectedTable.id : undefined,
      },
    });
    // Navigate to menu page
    // navigate(`/menu/${menuId}`);
  };

  const enabledOptions = orderTypeOptions.filter((option) => {
    if (option.value === "TAKE_AWAY") {
      return hasTakeAwayMenu;
    }

    if (option.value === "DINE_IN") {
      return hasDineInMenu;
    }

    return false;
  });

  return (
    <main className="flex flex-col items-center justify-end md:justify-center h-screen bg-muted">
      <Card className="w-full !bg-transparent overflow-hidden border-none max-md:rounded-t-2xl md:max-w-[60%]">
        <CardHeader className="p-0">
          <Hero />
        </CardHeader>
        <CardContent className="bg-background">
          {step === "ORDER-TYPE" && (
            <div className="flex flex-col gap-4">
              <CardDescription className="text-center">
                How would you like to order?
              </CardDescription>

              {enabledOptions.length > 1 ? (
                <div className="grid grid-cols-2 gap-4">
                  {enabledOptions.map((option) => (
                    <Button
                      key={option.value}
                      variant={"outline"}
                      onClick={() => handleOrderTypeSelection(option.value)}
                      className="h-24 flex items-center gap-3"
                      disabled={option.value === "DINE_IN" && !hasTables}
                    >
                      {option.icon}
                      <span className="font-medium">{option.label}</span>
                    </Button>
                  ))}
                </div>
              ) : (
                <p>This location isn't taking orders at the moment</p>
              )}
            </div>
          )}

          {step === "TABLE" && (
            <div className="flex flex-col gap-4">
              <CardDescription className="text-center">
                Please select where you're seated:
              </CardDescription>
              <select
                className="border-2 bg-background text-foreground rounded-md p-2 w-full"
                value={tableId ? tableId : ""}
                onChange={(e) => handleTableSelection(e.target.value)}
              >
                <option disabled value="">
                  Please select a table
                </option>
                {tables?.map((table) => (
                  <option key={table.name} value={table.id}>
                    {table.tableIdentifier}
                  </option>
                ))}
              </select>

              {selectedTable && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    handleTableSelection(selectedTable.id);
                  }}
                >
                  Select {selectedTable.tableIdentifier}
                </Button>
              )}
            </div>
          )}

          {step === "MENU" && (
            <div className="flex flex-col gap-4">
              <CardDescription className="text-center">
                Which menu would you like to order from?
                <br />
                You can order items from multiple menus at once.
              </CardDescription>

              <div className="grid gap-4 max-h-[40vh] overflow-y-auto">
                {availableMenus.map((menu) => (
                  <Button
                    key={menu.id}
                    variant={"outline"}
                    className="cursor-pointer group hover:!text-primary flex flex-col gap-1 py-4 h-max !min-h-20 items-start justify-center"
                    onClick={() => handleMenuSelection(menu.id)}
                  >
                    <span className="text-lg font-medium">{menu.name}</span>
                    {menu.description && (
                      <span className="text-muted-foreground group-hover:text-primary whitespace-pre-wrap text-left">
                        {menu.description}
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
};
